class PairedProductsManager {
  constructor() {
    this.initialised = false;
    this.retryDelay = 250;
    this.maxRetries = 3;
  }

  hasSubscription(product) {
    if (!product || !Array.isArray(product.variants)) return false;

    return product.variants.some((variant) =>
      variant &&
      variant.available &&
      Array.isArray(variant.selling_plan_allocations) &&
      variant.selling_plan_allocations.length > 0
    );
  }

  init() {
    if (this.initialised) return;

    this.initialised = true;

    document.addEventListener("click", (e) => {
      const heading = e.target.closest(".paired-product-heading");

      if (heading) {
        const block = heading.closest(".paired-product-recommendation");

        if (block) {
          block.classList.toggle("collapsed");
        }

        return;
      }

      const btn = e.target.closest(".paired-product-add");

      if (!btn || btn.disabled || btn.classList.contains("sold-out")) return;

      e.preventDefault();

      const variantId = btn.dataset.variant;
      if (!variantId) return;

      const sellingPlan = btn.dataset.sellingPlan || "";

      btn.disabled = true;
      btn.textContent = "Adding...";

      const payload = {
        id: parseInt(variantId, 10),
        quantity: 1
      };

      if (sellingPlan) {
        payload.selling_plan = parseInt(sellingPlan, 10);
      }

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          items: [payload]
        })
      })
        .then(() => {
          const cartUrl =
            window.routes && window.routes.cart_url
              ? window.routes.cart_url
              : "/cart";

          const isCartPage = window.location.pathname === cartUrl;

          if (isCartPage) {
            window.location.reload();
            return;
          }

          document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
        })
        .catch(() => {
          btn.disabled = false;
          btn.textContent = sellingPlan ? "Add Subscription" : "Add to cart";
        });
    });
  }

  formatMoney(price) {
    if (typeof price !== "number") return "";

    return (price / 100).toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP"
    });
  }

  getImageUrl(product) {
    if (!product) return "";

    if (typeof product.featured_image === "string") {
      return product.featured_image;
    }

    if (product.featured_image && typeof product.featured_image === "object") {
      if (product.featured_image.url) return product.featured_image.url;
      if (product.featured_image.src) return product.featured_image.src;
    }

    if (Array.isArray(product.images) && product.images.length) {
      const firstImage = product.images[0];

      if (typeof firstImage === "string") {
        return firstImage;
      }

      if (firstImage && typeof firstImage === "object") {
        if (firstImage.url) return firstImage.url;
        if (firstImage.src) return firstImage.src;
      }
    }

    return "";
  }

  removeBlock(block) {
    if (!block || !block.isConnected) return;

    const wrapper = block.closest(".cart-row-wrapper");

    block.remove();

    if (wrapper) {
      wrapper.classList.add("no-paired-product");
    }
  }

  renderBlock(block, product) {
    const container = block.querySelector(".paired-product-inner");

    if (!container) {
      this.removeBlock(block);
      return;
    }

    if (!product || !Array.isArray(product.variants) || !product.variants.length) {
      this.removeBlock(block);
      return;
    }

    const variant =
      product.variants.find((v) => v && v.available) || product.variants[0];

    if (!variant) {
      this.removeBlock(block);
      return;
    }

    const imageUrl = this.getImageUrl(product);
    const title = product.title || "";
    const productUrl = product.url || `/products/${product.handle}`;
    const sellingPlan = block.dataset.sellingPlan || "";
    const sourceProductTitle = block.dataset.sourceProductTitle || "";
    const price = this.formatMoney(variant.price);

    container.innerHTML = `
      <div class="paired-product-card">
        ${
          imageUrl
            ? `
              <a href="${productUrl}">
                <img
                  src="${imageUrl}"
                  class="paired-product-image"
                  alt="${title.replace(/"/g, "&quot;")}"
                  loading="lazy"
                >
              </a>
            `
            : ""
        }

        <div class="paired-product-details">
          <a href="${productUrl}" class="paired-product-title">
            ${title}
          </a>

          ${
            sourceProductTitle
              ? `
                <div class="paired-product-synergy">
                  Synergises well with ${sourceProductTitle}
                </div>
              `
              : ""
          }

          <div class="paired-product-price">
            ${price}
          </div>

          ${
            variant.available
              ? `
                <button
                  class="paired-product-add"
                  data-variant="${variant.id}"
                  data-selling-plan="${sellingPlan}"
                >
                  ${sellingPlan ? "Add Subscription" : "Add to cart"}
                </button>
              `
              : `<button class="paired-product-add sold-out" disabled>Back Soon</button>`
          }
        </div>
      </div>
    `;

    if (container.children.length > 0) {
      container.dataset.loaded = "true";
      container.dataset.loading = "false";
      container.dataset.retryCount = "0";
    } else {
      this.removeBlock(block);
    }
  }

  fetchForBlock(block) {
    const productId = block.dataset.productId;
    const container = block.querySelector(".paired-product-inner");

    if (!productId || !container) return Promise.resolve();
    if (container.dataset.loaded === "true") return Promise.resolve();
    if (container.dataset.loading === "true") return Promise.resolve();

    const retryCount = parseInt(container.dataset.retryCount || "0", 10);

    container.dataset.loading = "true";

    return fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=related`)
      .then((response) => response.json())
      .then((data) => {
        container.dataset.loading = "false";

        if (!data || !Array.isArray(data.products) || !data.products.length) {
          if (retryCount < this.maxRetries) {
            container.dataset.retryCount = String(retryCount + 1);

            setTimeout(() => {
              this.fetchForBlock(block);
            }, this.retryDelay);

            return;
          }

          this.removeBlock(block);
          return;
        }

        const sellingPlan = block.dataset.sellingPlan || "";

        if (sellingPlan) {
          const validProduct = data.products.find((product) =>
            this.hasSubscription(product)
          );

          if (validProduct) {
            this.renderBlock(block, validProduct);
          } else {
            this.removeBlock(block);
          }

          return;
        }

        const firstAvailable = data.products.find((product) =>
          product.variants &&
          product.variants.some((variant) => variant && variant.available)
        );

        if (firstAvailable) {
          this.renderBlock(block, firstAvailable);
        } else {
          this.removeBlock(block);
        }
      })
      .catch(() => {
        container.dataset.loading = "false";

        if (retryCount < this.maxRetries) {
          container.dataset.retryCount = String(retryCount + 1);

          setTimeout(() => {
            this.fetchForBlock(block);
          }, this.retryDelay);

          return;
        }

        this.removeBlock(block);
      });
  }

  load(root = document) {
    const blocks = root.querySelectorAll(".paired-product-recommendation[data-product-id]");

    if (!blocks.length) return;

    blocks.forEach((block) => {
      this.fetchForBlock(block);
    });
  }

  loadAfterRender(root = document) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.load(root);
      });
    });
  }
}

window.pairedProductsManager =
  window.pairedProductsManager || new PairedProductsManager();

const pairedProductsManager = window.pairedProductsManager;

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;

    const cartUrl =
      window.routes && window.routes.cart_url
        ? window.routes.cart_url
        : "/cart";

    this.isCartPage = window.location.pathname === cartUrl;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
    this.drawerInitialised = false;
  }

  connectedCallback() {
    pairedProductsManager.init();

    this.init();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");

        const closeButton = this.closeButton();

        if (closeButton) {
          closeButton.focus();
        }
      }

      pairedProductsManager.loadAfterRender(document);
    });

    pairedProductsManager.loadAfterRender(document);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";

    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;

      if (this.mainTrigger) {
        this.mainTrigger.focus();
      }

      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;

      const closeButton = this.closeButton();

      if (closeButton) {
        closeButton.focus();
      }

      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();

    if (this.drawer) {
      this.drawer.classList.toggle(this.classDrawerActive);
    }

    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    if (this.drawerInitialised) return;

    this.drawerInitialised = true;

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";

      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && first && last && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && first && last && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );

      if (sectionHTML !== null) {
        sectionElement.innerHTML = sectionHTML;
      }
    });

    const drawerTitle = document.getElementById("CartDrawer-TotalQty");

    if (drawerTitle) {
      drawerTitle.textContent = "Your Cart";
    }

    pairedProductsManager.loadAfterRender(document);

    document.dispatchEvent(new CustomEvent("cart-drawer:updated"));

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();

        if (this.isOpen) {
          const closeButton = this.closeButton();

          if (closeButton) {
            closeButton.focus();
          }
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return null;

    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const element = parsedHTML.querySelector(selector);

    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();

    const firstSection = sectionsToRender[0].id;
    const secondSection = sectionsToRender[1].id;

    fetch(`${window.Shopify.routes.root}?sections=${firstSection},${secondSection}`)
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };

        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawerSection);
}

if (typeof CartItems !== "undefined" && !customElements.get("cart-drawer-items")) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }

    renderContents(parsedState) {
      super.renderContents(parsedState);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          pairedProductsManager.loadAfterRender(document);
        });
      });
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}